<template>
  <div class="relative w-full h-full">
    <AspectRatio
      :ratio="aspectRatioValue"
      :class="[
        'overflow-hidden',
        // Only add rounded corners to the container if it's a video or has overlay content
        item.type === 'video' ? 'rounded-lg' : '',
      ]"
    >
      <template v-if="item.type === 'image'">
        <div class="relative w-full h-full cursor-pointer" @click.stop="handleClick">
          <NuxtImg
            :src="item.src"
            :alt="`Image ${index + 1}`"
            :class="[
              'w-full h-full',
              // Only add rounded corners to the image itself
              'rounded-lg',
              preserveRatio ? 'object-contain' : 'object-cover',
            ]"
            loading="lazy"
          />
        </div>
      </template>
      <template v-else>
        <div
          class="relative w-full h-full cursor-pointer"
          @click.stop="handleVideoContainerClick"
          @dblclick.stop="handleDoubleClick"
        >
          <video
            ref="videoRef"
            :src="item.src"
            :class="[
              'absolute inset-0 w-full h-full rounded-lg',
              preserveRatio ? 'object-contain' : 'object-cover',
            ]"
            preload="metadata"
            :loop="isSingleItem"
            @play="handlePlay"
            @pause="handlePause"
          >
            Your browser does not support the video tag.
          </video>
          <NuxtImg
            v-if="item.cover_photo && !isPlaying"
            :src="item.cover_photo"
            :alt="`Video ${index + 1} cover`"
            :class="[
              'absolute inset-0 w-full h-full rounded-lg',
              preserveRatio ? 'object-contain' : 'object-cover',
            ]"
            loading="lazy"
          />
          <div
            v-if="!isPlaying && isSingleItem"
            class="absolute inset-0 flex flex-col items-center justify-center"
          >
            <!-- Dark gradient overlay -->
            <div class="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60"></div>

            <!-- Play button -->
            <button
              class="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm hover:bg-white/40 transition-colors"
              @click.stop="handlePlayButtonClick"
            >
              <Icon name="heroicons:play-solid" class="h-8 w-8 text-white" />
            </button>
          </div>
        </div>
      </template>
    </AspectRatio>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { AspectRatio } from '@/components/ui/aspect-ratio'
import { useLogger } from '@/composables/useLogger'
import type { MediaItem } from './MediaGallery.vue'
import { computed, ref } from 'vue'
import EmbeddedText from './EmbeddedText.vue'

const logger = useLogger()
const videoRef = ref<HTMLVideoElement | null>(null)
const lastClickTime = ref(0)

const props = withDefaults(
  defineProps<{
    item: MediaItem
    index: number
    isPlaying: boolean
    aspectRatio: string
    preserveRatio?: boolean
    isSingleItem?: boolean
    bubbleId?: string
  }>(),
  {
    preserveRatio: false,
    isSingleItem: false,
    bubbleId: undefined,
  }
)

const emit = defineEmits<{
  play: []
  click: []
}>()

// Convert aspect ratio string to number (e.g., "16/9" -> 16/9)
const aspectRatioValue = computed(() => {
  try {
    const parts = props.aspectRatio.split('/')
    if (parts.length !== 2) {
      return 1 // Default to square if invalid format
    }

    const width = Number(parts[0])
    const height = Number(parts[1])

    if (isNaN(width) || isNaN(height) || height === 0) {
      return 1 // Default to square if invalid numbers
    }

    return width / height
  } catch (err) {
    logger.logError('MediaItem: Error parsing aspect ratio', err, {
      aspectRatio: props.aspectRatio,
    })
    return 1 // Default to square on error
  }
})

const handleClick = () => {
  logger.debug('🎯 MediaItem: Click event triggered', {
    type: props.item.type,
    index: props.index,
    src: props.item.src,
  })
  emit('click')
}

const handleVideoContainerClick = () => {
  if (props.isSingleItem) {
    if (props.isPlaying) {
      // If video is playing, pause it (only for single items)
      videoRef.value?.pause()
    } else {
      // If video is not playing, open gallery
      emit('click')
    }
  } else {
    // For multi-item galleries, always open fullscreen
    emit('click')
  }
}

const handleDoubleClick = () => {
  if (videoRef.value) {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      videoRef.value.requestFullscreen()
    }
  }
}

const handlePlayButtonClick = (event: Event) => {
  event.stopPropagation()
  if (videoRef.value) {
    videoRef.value.play()
  }
}

const handlePlay = () => {
  logger.debug('🎯 MediaItem: Play event triggered', {
    index: props.index,
    src: props.item.src,
  })
  emit('play')
}

const handlePause = () => {
  logger.debug('🎯 MediaItem: Pause event triggered', {
    index: props.index,
    src: props.item.src,
  })
}
</script>
